import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getUsciteMaterialeLavorato, createUscitaMaterialeLavorato, updateUscitaMaterialeLavorato,
  getCerRifiutiProdotti,
  type UscitaMaterialeLavorato, type CerRifiutoProdotto,
} from '../../api/settings';
import SettingsCrudSection, { type FieldDef, type FieldOption } from './SettingsCrudSection';

export default function UsciteMaterialeLavoratoSettings() {
  const { t } = useTranslation();
  const [items, setItems] = useState<UscitaMaterialeLavorato[]>([]);
  const [cerOptions, setCerOptions] = useState<CerRifiutoProdotto[]>([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    void (async () => {
      setLoading(true);
      try { setItems(await getUsciteMaterialeLavorato(false)); } finally { setLoading(false); }
    })();
  }, []);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => { void (async () => { setCerOptions(await getCerRifiutiProdotti(true)); })(); }, []);

  // V47: l'uscita referenzia il rifiuto per ID (più righe possono avere lo stesso
  // CER con descrizioni diverse). Value = id, label = "CER — descrizione".
  const cerFieldOptions: FieldOption[] = cerOptions.map((c) => ({
    value: c.id, label: c.nome_descrittivo ? `${c.cer} — ${c.nome_descrittivo}` : c.cer,
  }));
  // Mappa id → label per la resa in tabella (copre anche rifiuti referenziati ma
  // non più attivi, risolti dai dati della riga stessa).
  const labelById = new Map<string, string>();
  for (const c of cerOptions) labelById.set(c.id, c.nome_descrittivo ? `${c.cer} — ${c.nome_descrittivo}` : c.cer);
  for (const it of items) labelById.set(it.cer_rifiuto_id, it.nome_descrittivo ? `${it.cer} — ${it.nome_descrittivo}` : it.cer);

  const fields: FieldDef[] = [
    { key: 'nome', label: t('settings.uscite.nome'), kind: 'text', required: true, placeholder: t('settings.uscite.nome_placeholder') },
    {
      key: 'cer_rifiuto_id', label: t('settings.uscite.cer_rifiuto'), kind: 'select', required: true,
      optionsKey: 'cer_rifiuto', display: (v) => labelById.get(v ?? '') ?? '—',
    },
  ];

  const banner = cerOptions.length === 0 ? (
    <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
      {t('settings.uscite.banner')}
    </div>
  ) : null;

  return (
    <SettingsCrudSection<UscitaMaterialeLavorato>
      description={t('settings.uscite.descrizione')}
      fields={fields}
      rows={items}
      loading={loading}
      reload={reload}
      dynamicOptions={{ cer_rifiuto: cerFieldOptions }}
      testidPrefix="uscita"
      entityLabel={t('settings.uscite.entita')}
      emptyMsg={t('settings.uscite.vuoto')}
      statoLabels={{ attivo: t('settings.uscite.stato_attiva'), disattivato: t('settings.uscite.stato_disattivata') }}
      banner={banner}
      onCreate={async (v) => { await createUscitaMaterialeLavorato({ nome: v.nome.trim(), cer_rifiuto_id: v.cer_rifiuto_id }); }}
      onUpdate={async (id, v) => { await updateUscitaMaterialeLavorato(id, { nome: v.nome.trim(), cer_rifiuto_id: v.cer_rifiuto_id }); }}
      onToggle={async (it) => { await updateUscitaMaterialeLavorato(it.id, { attivo: !it.attivo }); }}
    />
  );
}
